import { notification } from "antd";
import cuid from "cuid";
import { filter, get, isNil, last, sortBy } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import { SEND_B2C_QUOTE } from "utils/api/graphql/mutations/quotes";
import {
  CREATE_SUBSCRIPTION,
  UPDATE_SUBSCRIPTION,
} from "utils/api/graphql/mutations/subscription";
import { OFFERS } from "utils/api/graphql/queries/offers";
import { KEYS } from "utils/constants";
import {
  buildCombinationAdditionalInfo,
  hasCombinationCodeChanged,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/combinationCode";
import { computeUpsellPricing } from "utils/helpers/lumineisUpsells";
import { filterByConditions } from "../../../..";
import {
  getCoverageFlags,
  getCurrentOptionalCoverageStep,
  getMainOfferQueryVariables,
  mergeModulesForWeight,
  PACK_WEIGHT,
  shouldContinueToPacks,
  shouldSendQuoteAtStep,
  UPSELL_WEIGHT,
} from "../utils/offerUpsells";
import { sendB2CQuote } from "../utils/sendB2CQuote";
import useInsurancePlans from "../../../Manual/Miltis/Comparison/Widgets/Upsells/hooks/useInsurancePlans";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const Upsells = ({
  processId,
  project,
  onNext,
  onBack,
  tracer,
  currentStatus,
  weight = UPSELL_WEIGHT,
}) => {
  const isPacksStep = weight === PACK_WEIGHT;
  const selectedProductId = get(project, "fields.lastSelected.0");
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [sendQuoteMutation] = useMutation(SEND_B2C_QUOTE);
  const [addLink] = useMutation(CREATE_LINK);
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = tErrors("API", { returnObjects: true });

  const [selectedModules, setSelectedModules] = useState(
    get(project, "fields.upsells", []),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [socleInsurancePlan, setSocleInsurancePlan] = useState({
    id: selectedProductId,
  });

  const [getOffers, { loading: offersLoading }] = useLazyQuery(OFFERS);
  const { selectedInsurancePlans, fetchMainOffers, loading } =
    useInsurancePlans({
      insurancePlan: { id: selectedProductId },
      getOffers,
      project,
      setSelectedModules,
      offerWhere: [{ offerInsurancePlans: { weight } }],
    });
  useQuery(OFFERS, {
    fetchPolicy: "no-cache",
    skip: !selectedProductId,
    variables: getMainOfferQueryVariables(selectedProductId),
    onCompleted: (offers) => {
      const mainOffer = get(offers, "offers.data.0");
      const mainPlan = get(mainOffer, "offerInsurancePlans", []).find(
        ({ main }) => main,
      )?.insurancePlan;
      if (mainPlan) {
        setSocleInsurancePlan(mainPlan);
      }
      fetchMainOffers(offers);
    },
  });

  const handleModule = ({ module }) => {
    const id = get(module, "insurancePlan.id");
    setSelectedModules((prev) => {
      const exist = get(prev, selectedProductId)?.find(
        ({ insurancePlan }) => insurancePlan.id === id,
      );
      return exist
        ? {
            ...prev,
            [selectedProductId]: get(prev, selectedProductId, []).filter(
              ({ insurancePlan }) => insurancePlan.id !== id,
            ),
          }
        : {
            ...prev,
            [selectedProductId]: [...get(prev, selectedProductId, []), module],
          };
    });
  };

  const onSubmit = async ({ ignore = false } = {}) => {
    setIsSubmitting(true);
    try {
      // Persist the combination-adjusted price for each selected pack so the saved
      // quote reflects the Alptis combination discount rather than standalone sums.
      const currentModules = ignore
        ? []
        : get(selectedModules, selectedProductId, []);
      const { costForModule } = computeUpsellPricing({
        modules: selectedInsurancePlans,
        selectedModules: currentModules,
      });
      const pricedModules = currentModules.map((module) => ({
        ...module,
        cost: costForModule(module),
      }));
      const mergedModules = mergeModulesForWeight({
        existingModules: get(
          project,
          `fields.upsells.${selectedProductId}`,
          [],
        ),
        modules: pricedModules,
        weight,
      });
      const adjustedSelectedModules = {
        ...get(project, "fields.upsells", {}),
        ...selectedModules,
        [selectedProductId]: mergedModules,
      };

      const coverageFlags = getCoverageFlags(project);
      const currentStep = getCurrentOptionalCoverageStep(isPacksStep);

      if (shouldContinueToPacks({ isPacksStep, ...coverageFlags })) {
        await onNext({
          payload: {
            fields: {
              ...project.fields,
              upsells: adjustedSelectedModules,
            },
          },
        });
        setIsSubmitting(false);
        return;
      }

      // Packs is the last optional step: always advance even if flags are stale.
      // Upsells without packs must still be the quote-send step.
      if (!isPacksStep && !shouldSendQuoteAtStep(currentStep, coverageFlags)) {
        setIsSubmitting(false);
        return;
      }

      const currentSubscriptionId = get(project, "fields.currentSubscription");
      const insurancePlanId =
        selectedProductId ||
        get(
          get(project, "subscriptions", []).find(
            ({ id }) => id === currentSubscriptionId,
          ),
          "insurancePlan.id",
        );

      if (!insurancePlanId) {
        setIsSubmitting(false);
        notification.error({
          message: API_ERRORS.GENERIC,
        });
        return;
      }

      const insurancePlans = [
        ...get(project, "fields.insurancePlans", []).filter(
          (id) => id !== insurancePlanId,
        ),
        insurancePlanId,
      ];
      const status = get(
        last(
          sortBy(
            filter(
              project.statuses,
              ({ status }) => status.process.id === processId,
            ),
            "createdDate",
          ),
        ),
        "status",
      );
      const possibleStatuses = get(status, "possibleStatuses", []);
      const [possibleStatus] = filterByConditions(
        sortBy(possibleStatuses, "order"),
        {
          ...project,
          status,
        },
      );

      const existingSubscription = get(project, "subscriptions", []).find(
        ({ insurancePlan }) => insurancePlan?.id === insurancePlanId,
      );

      const projectWithUpsells = {
        ...project,
        fields: {
          ...get(project, "fields", {}),
          upsells: adjustedSelectedModules,
        },
      };

      const resolveCombinationInfo = (insurancePlan = {}) =>
        buildCombinationAdditionalInfo({
          socleInsurancePlan: {
            ...socleInsurancePlan,
            ...insurancePlan,
            id: insurancePlanId,
            identifier:
              get(insurancePlan, "identifier") ||
              get(socleInsurancePlan, "identifier"),
            alias:
              get(insurancePlan, "alias") || get(socleInsurancePlan, "alias"),
          },
          project: projectWithUpsells,
          socleInsurancePlanId: insurancePlanId,
          existingAdditionalInfo: get(
            existingSubscription,
            "additionalInfo",
            {},
          ),
        });

      const finalizeSubscription = async (subscriptionId, insurancePlan) => {
        if (subscriptionId) {
          localStorage.setItem(KEYS.SUBSCRIPTION_ID, subscriptionId);
        }

        // Do not set project.locked here: Detail.onNext evaluates the next
        // status against the merged payload, and locked:true can block the
        // Packs → next transition.
        await onNext({
          payload: {
            visible: true,
            fields: {
              ...project.fields,
              insurancePlans,
              lastSelected: [insurancePlanId],
              ...(!isNil(possibleStatus?.nextStatus) && {
                nextQuoteStatus: possibleStatus.nextStatus,
              }),
              currentSubscription: subscriptionId,
              upsells: adjustedSelectedModules,
            },
          },
          onCompleted: async () => {
            try {
              await sendB2CQuote({
                project,
                processId,
                insurancePlanId,
                subscriptionId,
                insurancePlan,
                possibleStatus,
                addLink,
                generateQuoteDocuments,
                sendQuoteMutation,
                onError: () => {
                  notification.error({
                    message: API_ERRORS.GENERIC,
                  });
                },
              });
            } finally {
              setIsSubmitting(false);
            }
          },
        });
      };

      if (existingSubscription?.id) {
        const additionalInfo = resolveCombinationInfo(
          existingSubscription.insurancePlan,
        );
        if (
          hasCombinationCodeChanged(
            additionalInfo,
            existingSubscription.additionalInfo,
          )
        ) {
          await updateSubscription({
            variables: {
              where: { id: existingSubscription.id },
              data: { additionalInfo },
            },
          });
        }
        await finalizeSubscription(
          existingSubscription.id,
          existingSubscription.insurancePlan,
        );
        return;
      }

      await new Promise((resolve, reject) => {
        addSubscription({
          variables: {
            data: {
              id: cuid(),
              project: { id: project.id },
              insurancePlan: {
                id: insurancePlanId,
              },
              additionalInfo: resolveCombinationInfo(socleInsurancePlan),
            },
          },
          onCompleted: ({
            addSubscription: { id: subscriptionId, insurancePlan },
          }) => {
            Promise.resolve(finalizeSubscription(subscriptionId, insurancePlan))
              .then(resolve)
              .catch(reject);
          },
          onError: reject,
        });
      });
    } catch {
      setIsSubmitting(false);
      notification.error({
        message: API_ERRORS.GENERIC,
      });
    }
  };

  if (offersLoading || loading) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        currentStatus={currentStatus}
        tracer={tracer}
        showCart
        showProfile
      />
      <View
        contact={get(project, "contact", {})}
        onNext={onSubmit}
        onBack={onBack}
        isSubmitting={isSubmitting}
        isPacks={isPacksStep}
        upsells={selectedInsurancePlans}
        selectedModules={selectedModules}
        projectId={project?.id}
        handleModule={handleModule}
        insurancePlanId={selectedProductId}
      />
    </>
  );
};

export default Upsells;
